import * as yup from "yup";
import { OrderType, PayoutType, TransportType } from "../types";

const alphabeticalRegex = /^[A-Za-zÀ-ÖØ-öø-ÿ\s-]+$/;
const phoneNumberRegex = /^0\d(\s?\d{2}){4}$/;
const zipCodeRegex = /^\d{5}$/;
const coordinatesRegex = /^-?\d+(\.\d+)?$/;

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const SUPPORTED_FORMATS = [
  "image/jpg",
  "image/jpeg",
  "image/png",
  "image/webp",
];

export const loginSchema = yup.object().shape({
  email: yup
    .string()
    .email("Veuillez renseigner un email valide")
    .required("Ce champs est requis"),
  password: yup.string().min(8).required("Ce champs est requis"),
});

export const adminSchema = yup.object().shape({
  firstName: yup
    .string()
    .min(2, "Le prénom est trop court")
    .matches(alphabeticalRegex, "Le prénom ne peut contenir que des lettres")
    .required("Ce champs est requis"),
  lastName: yup
    .string()
    .min(2, "Le nom est trop court")
    .matches(alphabeticalRegex, "Le nom ne peut contenir que des lettres")
    .required("Ce champs est requis"),
  phone: yup
    .string()
    .matches(phoneNumberRegex, "Veuillez renseigner un numéro valide")
    .required("Ce champs est requis"),
  email: yup
    .string()
    .email("Veuillez renseigner un email valide")
    .required("Ce champs est requis"),
  password: yup.string().min(8).required("Ce champs est requis"),
});

export const restaurantSchema = yup.object().shape({
  displayName: yup
    .string()
    .min(2, "Le nom est trop court")
    .required("Ce champs est requis"),
  description: yup.string().required("Ce champs est requis"),
  email: yup
    .string()
    .email("Veuillez renseigner un email valide")
    .required("Ce champs est requis"),
  city: yup.string().required("Ce champs est requis"),

  orderTypes: yup
    .array()
    .of(
      yup
        .string()
        .oneOf(
          Object.values(OrderType),
          "Veuillez renseigner une valeur valide"
        )
    )
    .min(1, "Veuillez sélectionner au moins un type de commande"),

  address: yup.object().shape({
    street: yup.string().required("Ce champs est requis"),
    zipCode: yup
      .string()
      .matches(zipCodeRegex, "Veuillez renseigner un code postal valide")
      .required("Ce champs est requis"),
    city: yup.string().required("Ce champs est requis"),
    coordinates: yup
      .array()
      .of(
        yup
          .string()
          .matches(coordinatesRegex, "Coordonnée invalide")
          .required("Ce champs est requis")
      )
      .length(2, "Veuillez renseigner longitude et latitude")
      .required("Ce champs est requis"),
  }),

  payout: yup.object().shape({
    type: yup
      .string()
      .oneOf(
        Object.values(PayoutType),
        "Veuillez renseigner une valeur valide"
      ),
    value: yup
      .number()
      .typeError("Veuillez renseigner un nombre")
      .min(0, "La valeur doit être positive")
      .required("Ce champs est requis"),
  }),

  referant: yup.object().shape({
    firstName: yup
      .string()
      .min(2, "Le prénom est trop court")
      .matches(alphabeticalRegex, "Le prénom ne peut contenir que des lettres")
      .required("Ce champs est requis"),
    lastName: yup
      .string()
      .min(2, "Le nom est trop court")
      .matches(alphabeticalRegex, "Le nom ne peut contenir que des lettres")
      .required("Ce champs est requis"),
    phone: yup
      .string()
      .matches(phoneNumberRegex, "Veuillez renseigner un numéro valide")
      .required("Ce champs est requis"),
    siret: yup
      .string()
      .length(14, "Le SIRET doit contenir 14 chiffres")
      .required("Ce champs est requis"),
  }),

  settings: yup.object().shape({
    allergensLink: yup.string().url("Veuillez renseigner une URL valide"),
  }),

  openingHours: yup
    .array()
    .of(
      yup.object().shape({
        day: yup
          .string()
          .oneOf([
            "monday",
            "tuesday",
            "wednesday",
            "thursday",
            "friday",
            "saturday",
            "sunday",
          ])
          .required("Jour requis"),
        shifts: yup
          .array()
          .of(
            yup.object().shape({
              startTime: yup.string().required("Heure de début requise"),
              endTime: yup.string().required("Heure de fin requise"),
            })
          )
          .min(1, "Veuillez ajouter au moins un créneau")
          .required("Shifts requis"),
      })
    )
    .min(1, "Veuillez ajouter au moins un jour")
    .required("Horaires requis"),

  password: yup
    .string()
    .min(8, "Le mot de passe est trop court")
    .required("Ce champs est requis"),
});

export const modifierOptionSchema = yup.object().shape({
  displayName: yup
    .string()
    .min(2, "Le nom est trop court")
    .required("Ce champ est requis"),

  price: yup
    .number()
    .typeError("Veuillez renseigner un nombre")
    .min(0, "prix invalide")
    .required("Ce champ est requis"),

  vatRatePercentage: yup
    .number()
    .typeError("Veuillez renseigner un nombre")
    .min(0, "Le taux TVA doit être positif")
    .max(100, "Le taux TVA doit être inférieur ou égal à 100")
    .required("Ce champ est requis"),

  quantityInfo: yup.object().shape({
    minPermitted: yup
      .number()
      .typeError("Veuillez renseigner un nombre")
      .min(0, "La quantité minimale doit être positive")
      .required("Ce champ est requis"),
    maxPermitted: yup
      .number()
      .typeError("Veuillez renseigner un nombre")
      .min(
        yup.ref("minPermitted"),
        "La quantité maximale doit être supérieure ou égale à la minimale"
      )
      .required("Ce champ est requis"),
  }),

  displayOrder: yup
    .number()
    .typeError("Veuillez renseigner un nombre")
    .min(0, "L'ordre d'affichage doit être positif")
    .notRequired(),
});

export const modifierGroupSchema = yup.object().shape({
  displayName: yup
    .string()
    .min(2, "Le nom est trop court")
    .required("Ce champ est requis"),

  restaurant: yup.string().required("Un restaurant est requis"),

  quantityInfo: yup.object().shape({
    minPermitted: yup
      .number()
      .typeError("Veuillez renseigner un nombre")
      .min(0, "La quantité minimale doit être positive")
      .required("Ce champ est requis"),

    maxPermitted: yup
      .number()
      .typeError("Veuillez renseigner un nombre")
      .min(
        yup.ref("minPermitted"),
        "La quantité maximale doit être supérieure ou égale à la minimale"
      )
      .required("Ce champ est requis"),
  }),

  modifierOptions: yup
    .array()
    .of(
      yup.object().shape({
        _id: yup.string().required(),
        displayName: yup.string().required(),
      })
    )
    .min(1, "Veuillez ajouter au moins une option"),

  displayOrder: yup
    .number()
    .typeError("Veuillez renseigner un nombre")
    .min(0, "L'ordre d'affichage doit être positif")
    .notRequired(),
});

export const itemSchema = yup.object().shape({
  displayName: yup
    .string()
    .min(2, "Le nom est trop court")
    .required("Ce champ est requis"),

  description: yup
    .string()
    .max(500, "La description ne peut pas dépasser 500 caractères")
    .notRequired(),

  restaurant: yup.string().required("Un restaurant est requis"),

  price: yup
    .number()
    .typeError("Veuillez renseigner un nombre")
    .min(0, "prix invalide")
    .required("Ce champ est requis"),

  vatRatePercentage: yup
    .number()
    .typeError("Veuillez renseigner un nombre")
    .min(0, "La TVA doit être positive")
    .max(100, "La TVA ne peut pas dépasser 100%")
    .required("Ce champ est requis"),

  headers: yup
    .array()
    .of(
      yup.object().shape({
        _id: yup.string().required(),
        displayName: yup.string().required(),
      })
    )
    .notRequired(),

  modifierGroups: yup
    .array()
    .of(
      yup.object().shape({
        _id: yup.string().required(),
        displayName: yup.string().required(),
      })
    )
    .notRequired(),

  displayOrder: yup
    .number()
    .typeError("Veuillez renseigner un nombre")
    .min(0, "L'ordre d'affichage doit être positif")
    .notRequired(),
});

export const headerSchema = yup.object().shape({
  displayName: yup
    .string()
    .min(2, "Le nom est trop court")
    .required("Ce champ est requis"),

  description: yup.string().min(2, "La description est trop courte"),

  displayOrder: yup
    .number()
    .typeError("Veuillez renseigner un nombre")
    .min(0, "L'ordre d'affichage doit être positif")
    .notRequired(),
});

export const citySchema = yup.object().shape({
  displayName: yup
    .string()
    .min(2, "Le nom est trop court")
    .required("Ce champ est requis"),

  coordinates: yup
    .array()
    .of(
      yup
        .number()
        .typeError("La coordonnée doit être un nombre")
        .required("Ce champ est requis")
        .test("is-valid-coordinate", "Coordonnée invalide", function (value) {
          const index = this.path.match(/\[(\d+)\]/)?.[1];
          if (index === undefined) return true;
          const num = Number(value);

          if (index === "0") {
            // Longitude first
            return num >= -180 && num <= 180
              ? true
              : this.createError({
                  message: "Longitude invalide (entre -180 et 180)",
                });
          }

          if (index === "1") {
            // Latitude second
            return num >= -90 && num <= 90
              ? true
              : this.createError({
                  message: "Latitude invalide (entre -90 et 90)",
                });
          }

          return true;
        })
    )
    .min(2, "Les coordonnées doivent contenir longitude et latitude")
    .max(2, "Les coordonnées doivent contenir longitude et latitude"),
});

export const riderSchema = yup.object().shape({
  firstName: yup
    .string()
    .min(2, "Le prénom est trop court")
    .matches(alphabeticalRegex, "Le prénom ne peut contenir que des lettres")
    .required("Ce champs est requis"),

  lastName: yup
    .string()
    .min(2, "Le nom est trop court")
    .matches(alphabeticalRegex, "Le nom ne peut contenir que des lettres")
    .required("Ce champs est requis"),

  email: yup
    .string()
    .email("Veuillez renseigner un email valide")
    .required("Ce champs est requis"),

  phone: yup
    .string()
    .matches(phoneNumberRegex, "Veuillez renseigner un numéro valide")
    .required("Ce champs est requis"),

  city: yup.string().required("Ce champs est requis"),

  address: yup.object().shape({
    street: yup.string().required("Ce champs est requis"),
    city: yup.string().required("Ce champs est requis"),
    zipCode: yup
      .string()
      .matches(zipCodeRegex, "Veuillez renseigner un code postal valide")
      .required("Ce champs est requis"),
  }),

  transportType: yup
    .string()
    .oneOf(
      Object.values(TransportType),
      "Veuillez renseigner un type de transport valide"
    )
    .required("Ce champs est requis"),

  password: yup
    .string()
    .min(8, "Le mot de passe est trop court")
    .required("Ce champs est requis"),
});

export const createAdSchema = yup.object({
  cities: yup
    .array()
    .of(yup.string().required())
    .min(1, "Veuillez sélectionner au moins une ville")
    .required(),

  image: yup
    .mixed<File>()
    .required("Veuillez ajouter une image")
    .test(
      "fileSize",
      "L’image est trop volumineuse (max 5MB)",
      (value) => !value || value.size <= MAX_FILE_SIZE
    )
    .test(
      "fileFormat",
      "Format d’image non supporté",
      (value) => !value || SUPPORTED_FORMATS.includes(value.type)
    ),
});

export const editAdSchema = createAdSchema.shape({
  image: yup
    .mixed<File>()
    .notRequired()
    .test(
      "fileSize",
      "L’image est trop volumineuse (max 5MB)",
      (value) => !value || value.size <= MAX_FILE_SIZE
    )
    .test(
      "fileFormat",
      "Format d’image non supporté",
      (value) => !value || SUPPORTED_FORMATS.includes(value.type)
    ),
});
